import {
    Component,
    Input,
    Output,
    EventEmitter,
    TemplateRef,
    ElementRef,
    HostListener,
    HostBinding,
    ViewChild,
    ChangeDetectorRef,
    Renderer2
} from '@angular/core';

import { TagModel } from '../../core/accessor';
import { TagRipple } from '../tag/tag-ripple.component';

// angular universal hacks
/* tslint:disable-next-line */
const /** @type {?} */ KeyboardEvent = ( /** @type {?} */((global as any))).KeyboardEvent;
const /** @type {?} */ MouseEvent = ( /** @type {?} */((global as any))).MouseEvent;

// mocking navigator
const /** @type {?} */ navigator = typeof window !== 'undefined' ? window.navigator : {
    userAgent: 'Chrome',
    vendor: 'Google Inc'
};

const /** @type {?} */ isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
export class TagComponent {
/**
 * \@name model {TagModel}
 */
public model: TagModel;
/**
 * \@name removable {boolean}
 */
public removable: boolean;
/**
 * \@name editable {boolean}
 */
public editable: boolean;
/**
 * \@name template {TemplateRef<any>}
 */
public template: TemplateRef<any>;
/**
 * \@name displayBy {string}
 */
public displayBy: string;
/**
 * \@name identifyBy {string}
 */
public identifyBy: string;
/**
 * \@name index {number}
 */
public index: number;
/**
 * \@name hasRipple
 */
public hasRipple: boolean;
/**
 * \@name disabled
 */
public disabled = false;
/**
 * \@name onSelect
 */
public onSelect: EventEmitter<TagModel> = new EventEmitter<TagModel>();
/**
 * \@name onRemove
 */
public onRemove: EventEmitter<TagModel> = new EventEmitter<TagModel>();
/**
 * \@name onBlur
 */
public onBlur: EventEmitter<TagModel> = new EventEmitter<TagModel>();
/**
 * \@name onKeyDown
 */
public onKeyDown: EventEmitter<any> = new EventEmitter<any>();
/**
 * \@name onTagEdited
 */
public onTagEdited: EventEmitter<TagModel> = new EventEmitter<TagModel>();
/**
 * \@name readonly {boolean}
 * @return {?}
 */
public get readonly(): boolean {
        return typeof this.model !== 'string' && this.model.readonly === true;
    };
/**
 * \@name editing
 */
public editing = false;
/**
 * \@name moving
 */
public moving: boolean;
/**
 * \@name rippleState
 */
public rippleState = 'none';
/**
 * \@name ripple {TagRipple}
 */
public ripple: TagRipple;
/**
 * @param {?} element
 * @param {?} renderer
 * @param {?} cdRef
 */
constructor(public element: ElementRef,
public renderer: Renderer2,
private cdRef: ChangeDetectorRef) {}
/**
 * \@name select
 * @param {?=} $event
 * @return {?}
 */
public select($event?: MouseEvent): void {
        if (this.readonly || this.disabled) {
            return;
        }

        if ($event) {
            $event.stopPropagation();
        }

        this.focus();

        this.onSelect.emit(this.model);
    }
/**
 * \@name remove
 * @param {?} $event
 * @return {?}
 */
public remove($event: MouseEvent): void {
        $event.stopPropagation();
        this.onRemove.emit(this);
    }
/**
 * \@name focus
 * @return {?}
 */
public focus(): void {
        this.element.nativeElement.focus();
    }
/**
 * @return {?}
 */
public move(): void {
        this.moving = true;
    }
/**
 * \@name keydown
 * @param {?} event
 * @return {?}
 */
public keydown(event: KeyboardEvent): void {
        if (this.editing) {
            event.keyCode === 13 ? this.disableEditMode(event) : undefined;
            return;
        }

        this.onKeyDown.emit({event, model: this.model});
    }
/**
 * \@name blink
 * @return {?}
 */
public blink(): void {
        const /** @type {?} */ classList = this.element.nativeElement.classList;
        classList.add('blink');

        setTimeout(() => classList.remove('blink'), 50);
    }
/**
 * \@name toggleEditMode
 * @return {?}
 */
public toggleEditMode(): void {
        if (this.editable) {
            this.editing ? undefined : this.activateEditMode();
        }
    }
/**
 * \@name onBlurred
 * @param {?} event
 * @return {?}
 */
public onBlurred(event: any): void {
        // Checks if it is editable first before handeling the onBlurred event in order to prevent
        // a bug in IE where tags are still editable with onlyFromAutocomplete set to true
		if (!this.editable) {
			return;
        }

        this.disableEditMode();

        const /** @type {?} */ value: string = event.target.innerText;
        const /** @type {?} */ result = typeof this.model === 'string' ? value :
            {...this.model, [this.displayBy]: value};

        this.onBlur.emit(result);
    }
/**
 * \@name getDisplayValue
 * @param {?} item
 * @return {?}
 */
public getDisplayValue(item: TagModel): string {
        return typeof item === 'string' ? item : item[this.displayBy];
    }
/**
 * @desc returns whether the ripple is visible or not
 * only works in Chrome
 * \@name isRippleVisible
 * @return {?}
 */
public get isRippleVisible(): boolean {
        return !this.readonly &&
            !this.editing &&
            isChrome &&
            this.hasRipple;
    }
/**
 * \@name getContentEditableText
 * @return {?}
 */
private getContentEditableText(): string {
        const /** @type {?} */ input = this.getContentEditable();

        return input ? input.innerText.trim() : '';
    }
/**
 * \@name setContentEditableText
 * @param {?} model
 * @return {?}
 */
private setContentEditableText(model: TagModel) {
        const /** @type {?} */ input = this.getContentEditable();
        const /** @type {?} */ value = this.getDisplayValue(model);

        input.innerText = value;
    }
/**
 * \@name
 * @return {?}
 */
private activateEditMode(): void {
        const /** @type {?} */ classList = this.element.nativeElement.classList;
        classList.add('tag--editing');

        this.editing = true;
    }
/**
 * \@name disableEditMode
 * @param {?=} $event
 * @return {?}
 */
private disableEditMode($event?: KeyboardEvent): void {
        const /** @type {?} */ classList = this.element.nativeElement.classList;
        const /** @type {?} */ input = this.getContentEditableText();

        this.editing = false;
        classList.remove('tag--editing');

        if (!input) {
            this.setContentEditableText(this.model);
            return;
        }

        this.storeNewValue(input);
        this.cdRef.detectChanges();

        if ($event) {
            $event.preventDefault();
        }
    }
/**
 * \@name storeNewValue
 * @param {?} input
 * @return {?}
 */
private storeNewValue(input: string): void {
        const /** @type {?} */ exists = (model: TagModel) => {
            return typeof model === 'string' ?
                model === input :
                model[this.displayBy] === input;
        };

        const /** @type {?} */ hasId = () => {
            return this.model[this.identifyBy] !== this.model[this.displayBy];
        };

        // if the value changed, replace the value in the model
        if (exists(this.model)) {
            return;
        }

        const /** @type {?} */ model = typeof this.model === 'string' ? input :
            {
                [this.identifyBy]: hasId() ? this.model[this.identifyBy] : input,
                [this.displayBy]: input
            };

        // emit output
        this.model = model;
        this.onTagEdited.emit(model);
    }
/**
 * \@name getContentEditable
 * @return {?}
 */
private getContentEditable(): HTMLInputElement {
        return this.element.nativeElement.querySelector('[contenteditable]');
    }
/**
 * \@name isDeleteIconVisible
 * @return {?}
 */
private isDeleteIconVisible(): boolean {
        return !this.readonly &&
                !this.disabled &&
                this.removable &&
                !this.editing;
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'tag',
    template: `
      <div (click)="select($event)"
           (dblclick)="toggleEditMode()"
           (mousedown)="rippleState='clicked'"
           (mouseup)="rippleState='none'"
           [ngSwitch]="!!template"
           [class.disabled]="disabled"
           [attr.tabindex]="-1"
           [attr.aria-label]="getDisplayValue(model)">

          <div *ngSwitchCase="true" [attr.contenteditable]="editing">
              <!-- CUSTOM TEMPLATE -->
              <ng-template
                  [ngTemplateOutletContext]="{ item: model, index: index }"
                  [ngTemplateOutlet]="template">
              </ng-template>
          </div>

          <div *ngSwitchCase="false" class="tag-wrapper">
              <!-- TAG NAME -->
              <div [attr.contenteditable]="editing"
                   [attr.title]="getDisplayValue(model)"
                   class="tag__text inline"
                   spellcheck="false"
                   (keydown.enter)="disableEditMode($event)"
                   (keydown.escape)="disableEditMode($event)"
                   (click)="editing ? $event.stopPropagation() : undefined"
                   (blur)="onBlurred($event)">
                  {{ getDisplayValue(model) }}
              </div>

              <!-- 'X' BUTTON -->
              <delete-icon
                  aria-label="Remove tag"
                  role="button"
                  (click)="remove($event)"
                  *ngIf="isDeleteIconVisible()">
              </delete-icon>
          </div>
      </div>

      <tag-ripple [state]="rippleState"
                  [attr.tabindex]="-1"
                  *ngIf="isRippleVisible">
      </tag-ripple>
    `,
    styles: [`
      :host,
      :host > div,
      :host > div:focus {
        outline: 0;
        overflow: hidden;
        -webkit-transition: opacity 1s;
        transition: opacity 1s;
        z-index: 1; }

      :host {
        max-width: 400px; }

      :host.blink {
        -webkit-animation: blink 0.3s normal forwards ease-in-out;
        animation: blink 0.3s normal forwards ease-in-out; }

      @-webkit-keyframes blink {
        0% {
          opacity: 0.3; } }

      @keyframes blink {
        0% {
          opacity: 0.3; } }

      :host .disabled {
        cursor: not-allowed; }

      :host [contenteditable='true'] {
        outline: 0; }

      .tag-wrapper {
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex; }

      .tag__text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap; }
    `]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'model': [{ type: Input },],
'removable': [{ type: Input },],
'editable': [{ type: Input },],
'template': [{ type: Input },],
'displayBy': [{ type: Input },],
'identifyBy': [{ type: Input },],
'index': [{ type: Input },],
'hasRipple': [{ type: Input },],
'disabled': [{ type: Input },],
'onSelect': [{ type: Output },],
'onRemove': [{ type: Output },],
'onBlur': [{ type: Output },],
'onKeyDown': [{ type: Output },],
'onTagEdited': [{ type: Output },],
'moving': [{ type: HostBinding, args: ['class.moving', ] },],
'ripple': [{ type: ViewChild, args: [TagRipple, ] },],
'keydown': [{ type: HostListener, args: ['keydown', ['$event'], ] },],
};
}

function TagComponent_tsickle_Closure_declarations() {
/** @type {?} */
TagComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TagComponent.ctorParameters;
/** @type {?} */
TagComponent.propDecorators;
/**
 * \@name model {TagModel}
 * @type {?}
 */
TagComponent.prototype.model;
/**
 * \@name removable {boolean}
 * @type {?}
 */
TagComponent.prototype.removable;
/**
 * \@name editable {boolean}
 * @type {?}
 */
TagComponent.prototype.editable;
/**
 * \@name template {TemplateRef<any>}
 * @type {?}
 */
TagComponent.prototype.template;
/**
 * \@name displayBy {string}
 * @type {?}
 */
TagComponent.prototype.displayBy;
/**
 * \@name identifyBy {string}
 * @type {?}
 */
TagComponent.prototype.identifyBy;
/**
 * \@name index {number}
 * @type {?}
 */
TagComponent.prototype.index;
/**
 * \@name hasRipple
 * @type {?}
 */
TagComponent.prototype.hasRipple;
/**
 * \@name disabled
 * @type {?}
 */
TagComponent.prototype.disabled;
/**
 * \@name onSelect
 * @type {?}
 */
TagComponent.prototype.onSelect;
/**
 * \@name onRemove
 * @type {?}
 */
TagComponent.prototype.onRemove;
/**
 * \@name onBlur
 * @type {?}
 */
TagComponent.prototype.onBlur;
/**
 * \@name onKeyDown
 * @type {?}
 */
TagComponent.prototype.onKeyDown;
/**
 * \@name onTagEdited
 * @type {?}
 */
TagComponent.prototype.onTagEdited;
/**
 * \@name editing
 * @type {?}
 */
TagComponent.prototype.editing;
/**
 * \@name moving
 * @type {?}
 */
TagComponent.prototype.moving;
/**
 * \@name rippleState
 * @type {?}
 */
TagComponent.prototype.rippleState;
/**
 * \@name ripple {TagRipple}
 * @type {?}
 */
TagComponent.prototype.ripple;
/** @type {?} */
TagComponent.prototype.element;
/** @type {?} */
TagComponent.prototype.renderer;
/** @type {?} */
TagComponent.prototype.cdRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
